import Phaser from 'phaser';
import { nameText } from '../../content/names';
import { NICKNAME_KEYS, NICKNAME_MAX } from '../../meta/online/nicknames';
import type { PlayerName } from '../../meta/online/schema';
import { haptics } from '../../platform/haptics';
import { nicknameOf, setNickname } from '../nicknames';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wipeTo } from './WipeScene';

/** The friend to nickname (from their card, CLUB > YOU > FRIENDS). */
export interface NicknameData {
  player: string;
  name: PlayerName;
  /** Where SAVE and BACK go (the friend's card); FRIENDS without. */
  back?: { scene: string; data?: object };
}

/** The keyboard: ten keys a row; SPACE and DEL take two each. */
const ROWS: readonly (readonly string[])[] = [
  [...NICKNAME_KEYS.slice(0, 10)],
  [...NICKNAME_KEYS.slice(10, 20)],
  [...NICKNAME_KEYS.slice(20, 26), 'SPACE', 'DEL'],
  [...NICKNAME_KEYS.slice(26)],
];
const WIDE = new Set(['SPACE', 'DEL']);
const KEY_W = 16;
const KEY_H = 16;
const GAP = 2;

/**
 * A friend's nickname (owner, 2026-10-26; src/meta/online/nicknames.ts): typed on a pixel keyboard
 * (or a real one), up to 10 capitals, digits and spaces. Only this player sees it: it stays in the
 * save and shows wherever that friend's name would. SAVE with nothing typed takes it away.
 */
export class NicknameScene extends Phaser.Scene {
  private player = '';
  private name: PlayerName = { kind: 'words', parts: [0, 0], number: 0 };
  private draft = '';
  private backTo: NicknameData['back'] = undefined;

  constructor() {
    super('Nickname');
  }

  init(data: NicknameData): void {
    this.player = data.player;
    this.name = data.name;
    this.backTo = data.back;
    this.draft = nicknameOf(data.player) ?? '';
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    // A real keyboard works too (the web game on a computer).
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Backspace') this.erase();
      else if (e.key === 'Enter') this.save();
      else if (e.key === 'Escape') this.back();
      else if (e.key === ' ') this.type(' ');
      else if (e.key.length === 1 && NICKNAME_KEYS.includes(e.key.toUpperCase())) {
        this.type(e.key.toUpperCase());
      }
    });
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'NICKNAME', 2);
    label(this, width / 2, 30, `For ${nameText(this.name)}`, 1, UI.text).setOrigin(0.5, 0);
    label(this, width / 2, 41, 'Only you see it.', 1, UI.dim).setOrigin(0.5, 0);

    // What is typed so far, big, with a cursor while there is room.
    const fy = 54;
    navyPanel(g, 4, fy, width - 8, 26);
    const shown = this.draft.length < NICKNAME_MAX ? `${this.draft}_` : this.draft;
    label(this, width / 2, fy + 6, shown, 2, UI.accent).setOrigin(0.5, 0);

    // The keys.
    const rowW = 10 * KEY_W + 9 * GAP;
    const left = Math.floor((width - rowW) / 2);
    let y = fy + 34;
    for (const row of ROWS) {
      let x = left;
      for (const key of row) {
        const w = WIDE.has(key) ? 2 * KEY_W + GAP : KEY_W;
        bevelButton(this, x, y, w, KEY_H, key, () => {
          if (key === 'DEL') this.erase();
          else this.type(key === 'SPACE' ? ' ' : key);
        });
        x += w + GAP;
      }
      y += KEY_H + GAP;
    }

    // Clear it, or keep it.
    y += 6;
    const half = Math.floor((width - 16) / 2);
    bevelButton(this, 6, y, half, 18, 'CLEAR', () => {
      this.draft = '';
      this.redraw();
    });
    bevelButton(this, 10 + half, y, half, 18, 'SAVE', () => this.save(), true);
    label(this, width / 2, y + 26, 'Save it empty for their name.', 1, UI.dim).setOrigin(0.5, 0);

    bevelButton(this, 6, height - 30, width - 12, 24, 'BACK', () => this.back());
  }

  private type(ch: string): void {
    if (this.draft.length >= NICKNAME_MAX) return;
    // No space first, and no two in a row.
    if (ch === ' ' && (this.draft === '' || this.draft.endsWith(' '))) return;
    this.draft += ch;
    this.redraw();
  }

  private erase(): void {
    if (!this.draft) return;
    this.draft = this.draft.slice(0, -1);
    this.redraw();
  }

  private save(): void {
    setNickname(this.player, this.draft);
    haptics.tap();
    this.back();
  }

  private back(): void {
    wipeTo(this, this.backTo?.scene ?? 'Friends', this.backTo?.data);
  }
}
